import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../components/apiconfig/api-config';
import { FaFilter, FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';
import PurchaseContractDetails from '../../modules/reports/country-report/country-wise-report';
import SalesReportswarehouse from '../../modules/reports/sales-reports-warehouse/sales-reports-warehouse';
import NoRecordFound from '../../components/common/no-record-found';
import { FINANCIAL_YEAR_OPTIONS } from '../../components/common/dateUtils';

const Dashboard = () => {
  const [commodityData, setCommodityData] = useState([]);
  const [vesselData, setVesselData] = useState([]);
  const [loading, setLoading] = useState({
    commodity: true
  });
  const [financialYear, setFinancialYear] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [commodityFilter, setCommodityFilter] = useState('');
  const [vesselFilter, setVesselFilter] = useState('');
  const [commoditiesList, setCommoditiesList] = useState([]);
  const [vesselsList, setVesselsList] = useState([]);
  const [showDateFilters, setShowDateFilters] = useState(false);

  const isFySelected = Boolean(financialYear);

  const fetchFilterOptions = async (commodity = '') => {
    try {
      const commPromise = axios.get(`${API_BASE_URL}/api/masters/dropdown-commodities`, { withCredentials: true });
      const vesselPromise = axios.get(`${API_BASE_URL}/api/masters/dropdown-vessel-names`, { 
        params: commodity ? { commodity } : {},
        withCredentials: true 
      });

      const [commRes, vesselRes] = await Promise.all([commPromise, vesselPromise]);
      setCommoditiesList(commRes.data.data || []);
      setVesselsList(vesselRes.data.data || []);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchCommodityData = async (start = null, end = null, commodity = null, vessel = null, fy = null) => {
    try {
      setLoading(prev => ({...prev, commodity: true}));
      const params = {};
      
      const activeFy = fy !== null ? fy : financialYear;
      if (activeFy) {
        params.financialYear = activeFy;
      }
      if (start) params.startDate = start;
      if (end) params.endDate = end;
      if (commodity) params.commodityName = commodity;
      if (vessel) params.vesselName = vessel;

      const response = await axios.get(`${API_BASE_URL}/api/dashboard`, {
        params,
        withCredentials: true
      });

      const data = response.data.data || [];
      const vData = response.data.vesselData || [];
      setCommodityData(data);
      setVesselData(vData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(prev => ({...prev, commodity: false}));
    }
  };

  useEffect(() => {
    fetchCommodityData();
    fetchFilterOptions(commodityFilter);
  }, []);

  useEffect(() => {
    fetchFilterOptions(commodityFilter);
  }, [commodityFilter]);

  const handleFinancialYearChange = (value) => {
    setFinancialYear(value);
    fetchCommodityData(startDate, endDate, commodityFilter, vesselFilter, value);
  };

  const handleDateFilter = () => {
    // Validate dates
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("End date cannot be before start date");
      return;
    }
    fetchCommodityData(startDate, endDate, commodityFilter, vesselFilter, financialYear);
  };

  const handleResetDateFilter = () => {
    setFinancialYear('');
    setStartDate('');
    setEndDate('');
    setCommodityFilter('');
    setVesselFilter('');
    fetchCommodityData(null, null, null, null, ''); 
  };


  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-0">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Dashboard Overview</h1>
      </div>

      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Inventory & Vessel Tracking</h2>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setShowDateFilters(!showDateFilters)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-[#10a5e9] rounded-md hover:bg-blue-100 text-sm w-full sm:w-auto justify-center font-medium"
            >
              <FaFilter />
              <span className="hidden sm:inline">{showDateFilters ? 'Hide Filters' : 'Show Filters'}</span>
              <span className="sm:hidden">{showDateFilters ? 'Hide' : 'Filters'}</span>
              {financialYear && (
                <span className="bg-[#10a5e9] text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                  FY: {financialYear}
                </span>
              )}
              {showDateFilters ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
            </button>
          </div>
        </div>

        {/* Filters (Global) */}
        {showDateFilters && (
          <div className="bg-gray-50 p-3 sm:p-4 border border-gray-200 rounded-lg mb-6">
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-end flex-wrap">
              {/* Financial Year Filter */}
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs sm:text-sm font-semibold text-gray-700">Financial Year</label>
                <select
                  value={financialYear}
                  onChange={(e) => handleFinancialYearChange(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-[#10a5e9] rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9] bg-white font-medium text-gray-800"
                >
                  <option value="">All Financial Years</option>
                  {FINANCIAL_YEAR_OPTIONS.map((opt) => (
                    <option key={opt.id} value={opt.value}>{opt.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs sm:text-sm font-medium text-gray-600">From Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9] bg-white"
                  max={endDate || undefined}
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs sm:text-sm font-medium text-gray-600">To Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9] bg-white"
                  min={startDate || undefined}
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs sm:text-sm font-medium text-gray-600">Commodity</label>
                <select
                  value={commodityFilter}
                  onChange={(e) => setCommodityFilter(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9] bg-white"
                >
                  <option value="">All Commodities</option>
                  {commoditiesList.map((c) => (
                    <option key={c.commodity_id} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-xs sm:text-sm font-medium text-gray-600">Vessel</label>
                <select
                  value={vesselFilter}
                  onChange={(e) => setVesselFilter(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-[#10a5e9] focus:border-[#10a5e9] bg-white"
                >
                  <option value="">{commodityFilter ? `Select Vessel for ${commodityFilter}` : "All Vessels"}</option>
                  {vesselsList.map((v, idx) => (
                    <option key={idx} value={v.vessel_name}>{v.vessel_name}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-row gap-2 w-full sm:w-auto mt-2 sm:mt-0 min-w-[200px]">
                <button
                  onClick={handleDateFilter}
                  className="flex-1 sm:flex-none px-4 py-2 bg-[#10a5e9] text-white hover:bg-[#0284c7] rounded-md text-sm font-medium transition-colors shadow-sm"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleResetDateFilter}
                  className="flex-1 sm:flex-none px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
            {financialYear && (
              <div className="flex items-center gap-1.5 text-xs text-blue-800 mt-2 bg-blue-50 px-2.5 py-1.5 rounded border border-blue-200 inline-flex">
                <FaInfoCircle className="text-[#10a5e9] flex-shrink-0" />
                <span>
                  Financial Year ({financialYear}) is active: Applied only to <strong>Purchase Contracts</strong>.
                </span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-8">
          {/* Commodity Section */}
          <div className="overflow-hidden">
            <h3 className="text-md sm:text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Commodity Overview</h3>

        {loading.commodity ? (
          <div className="flex flex-col items-center justify-center h-48">
            <div className="animate-spin h-8 w-8 sm:h-10 sm:w-10 border-4 border-[#10a5e9] border-t-transparent rounded-full mb-2" />
            <span className="text-gray-600 text-xs sm:text-sm">Loading commodity data...</span>
          </div>
        ) : commodityData.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm border-collapse border border-gray-300 min-w-[600px]">
                <thead className="bg-gray-100">
                  <tr className="text-left text-gray-600">
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Commodity</span>
                      <span className="sm:hidden">Item</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Purchase Contracted (MT)</span>
                      <span className="sm:hidden">Purchase</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Sales Contracted (MT)</span>
                      <span className="sm:hidden">Sales</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Cancelled Qty. (MT)</span>
                      <span className="sm:hidden">Cancelled</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Stock Adjustment Note (SAN)</span>
                      <span className="sm:hidden">SAN</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">In Stock (MT)</span>
                      <span className="sm:hidden">Stock</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {commodityData.map((item) => (
                    <tr key={item.commodity_id} className="hover:bg-blue-50">
                      <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{item.commodity_name}</td>
                      <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.total_import_qty).toFixed(3)}</td>
                      <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.total_sales_qty).toFixed(3)}</td>
                      <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.total_cancelled_qty || 0).toFixed(3)}</td>
                      <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">
                        <span className={item.total_san_qty < 0 ? 'text-red-500 font-medium' : item.total_san_qty > 0 ? 'text-green-500 font-medium' : ''}>
                          {Number(item.total_san_qty || 0).toFixed(3)}
                        </span>
                      </td>
                      <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.remaining_qty).toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-100 font-bold text-blue-900">
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">Total</td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {commodityData
                        .reduce((sum, item) => sum + Number(item.total_import_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {commodityData
                        .reduce((sum, item) => sum + Number(item.total_sales_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {commodityData
                        .reduce((sum, item) => sum + Number(item.total_cancelled_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {commodityData
                        .reduce((sum, item) => sum + Number(item.total_san_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {commodityData
                        .reduce((sum, item) => sum + Number(item.remaining_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <NoRecordFound 
            message="No commodity data available"
            subMessage={startDate || endDate ? "No data found for the selected date range. Try adjusting your date filters." : "No commodity data is currently available in the system."}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            }
          />
        )}
          </div>

          {/* Vessel Section */}
          <div className="overflow-hidden">
            <h3 className="text-md sm:text-lg font-semibold text-gray-700 mb-3 border-b pb-2">Vessel Overview</h3>

        {loading.commodity ? (
          <div className="flex flex-col items-center justify-center h-48">
            <div className="animate-spin h-8 w-8 sm:h-10 sm:w-10 border-4 border-[#10a5e9] border-t-transparent rounded-full mb-2" />
            <span className="text-gray-600 text-xs sm:text-sm">Loading vessel data...</span>
          </div>
        ) : vesselData && vesselData.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm border-collapse border border-gray-300 min-w-[700px]">
                <thead className="bg-gray-100">
                  <tr className="text-left text-gray-600">
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Commodity</span>
                      <span className="sm:hidden">Item</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Vessel</span>
                      <span className="sm:hidden">Vessel</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Purchase Contracted (MT)</span>
                      <span className="sm:hidden">Purchase</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Sales Contracted (MT)</span>
                      <span className="sm:hidden">Sales</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Cancelled Qty. (MT)</span>
                      <span className="sm:hidden">Cancelled</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">Stock Adjustment Note (SAN)</span>
                      <span className="sm:hidden">SAN</span>
                    </th>
                    <th className="px-2 sm:px-4 py-2 border border-gray-300">
                      <span className="hidden sm:inline">In Stock (MT)</span>
                      <span className="sm:hidden">Stock</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(vesselData.reduce((acc, item) => {
                    if (!acc[item.commodity_name]) acc[item.commodity_name] = [];
                    acc[item.commodity_name].push(item);
                    return acc;
                  }, {})).map((commodityName, cIdx) => {
                    const group = vesselData.filter(item => item.commodity_name === commodityName);
                    const subtotalImport = group.reduce((sum, item) => sum + Number(item.total_import_qty || 0), 0);
                    const subtotalSales = group.reduce((sum, item) => sum + Number(item.total_sales_qty || 0), 0);
                    const subtotalCancelled = group.reduce((sum, item) => sum + Number(item.total_cancelled_qty || 0), 0);
                    const subtotalSan = group.reduce((sum, item) => sum + Number(item.total_san_qty || 0), 0);
                    const subtotalRemaining = group.reduce((sum, item) => sum + Number(item.remaining_qty || 0), 0);

                    return (
                      <React.Fragment key={`group_${cIdx}`}>
                        {group.map((item, idx) => (
                          <tr key={`${item.commodity_id}_${idx}`} className="hover:bg-blue-50">
                            <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{item.commodity_name}</td>
                            <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">
                              {item.vessel_name === 'Pending Vessel Assignment' ? (
                                <span className="text-orange-500 font-medium italic">{item.vessel_name}</span>
                              ) : (
                                <span>{item.vessel_name}</span>
                              )}
                            </td>
                            <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.total_import_qty).toFixed(3)}</td>
                            <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.total_sales_qty).toFixed(3)}</td>
                            <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.total_cancelled_qty || 0).toFixed(3)}</td>
                            <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">
                              <span className={item.total_san_qty < 0 ? 'text-red-500 font-medium' : item.total_san_qty > 0 ? 'text-green-500 font-medium' : ''}>
                                {Number(item.total_san_qty || 0).toFixed(3)}
                              </span>
                            </td>
                            <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm">{Number(item.remaining_qty).toFixed(3)}</td>
                          </tr>
                        ))}
                        {/* Subtotal row */}
                        <tr className="bg-gray-50 font-semibold text-gray-700">
                          <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm text-right" colSpan={2}>
                            {commodityName} Total
                          </td>
                          <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm text-blue-600">{subtotalImport.toFixed(3)}</td>
                          <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm text-blue-600">{subtotalSales.toFixed(3)}</td>
                          <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm text-blue-600">{subtotalCancelled.toFixed(3)}</td>
                          <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm text-blue-600">{subtotalSan.toFixed(3)}</td>
                          <td className="px-2 sm:px-4 py-2 border border-gray-200 text-xs sm:text-sm text-blue-600">{subtotalRemaining.toFixed(3)}</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-blue-100 font-bold text-blue-900">
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm" colSpan={2}>Total</td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {vesselData
                        .reduce((sum, item) => sum + Number(item.total_import_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {vesselData
                        .reduce((sum, item) => sum + Number(item.total_sales_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {vesselData
                        .reduce((sum, item) => sum + Number(item.total_cancelled_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {vesselData
                        .reduce((sum, item) => sum + Number(item.total_san_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                    <td className="px-2 sm:px-4 py-2 border border-gray-300 text-xs sm:text-sm">
                      {vesselData
                        .reduce((sum, item) => sum + Number(item.remaining_qty || 0), 0)
                        .toFixed(3)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        ) : (
          <NoRecordFound 
            message="No vessel data available"
            subMessage={startDate || endDate ? "No data found for the selected date range. Try adjusting your date filters." : "No vessel data is currently available in the system."}
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            }
          />
        )}
      </div>
        </div>
      </div>

      {/* Purchase Contracts Section - Now using the separate component */}
      <div className="">
        <PurchaseContractDetails />
      </div>
      
      {/* Sales Reports Section */}
      <div className="">
        <SalesReportswarehouse />
      </div>
    </div>
  );
};

export default Dashboard;